import { Map, List } from 'immutable'
import { VideoChaptersButton } from './video-chapters-button'

const { videojs } = global
const Plugin = videojs.getPlugin('plugin')

/**
 * @typedef {Map<Map<string>|string>} Chapters keys are language codes
 */

/**
 * @typedef {import('immutable').List<string>} UserLanguages
 */

/**
 * @typedef {Object} PluginOptions
 * @property {Map<string>} options.staticText list of translations
 * @property {UserLanguages} options.userLanguages
 * @property {Chapters} options.chapters
 * @property {string} options.appLanguage
 * @property {Object} options.auth authentication object with JWT
 */

/**
 * @class
 * @memberof VideoChaptersPluginNamespace
 */
export default class VideoChaptersPlugin extends Plugin {
  static PLUGIN_NAME = 'VideoChapters'

  #options

  #currentLanguage

  #auth

  /**
   * Create a VideoChaptersPlugin plugin instance.
   *
   * @param  {Player} player
   *         A Video.js Player instance.
   *
   * @param  {PluginOptions} [options=[]]
   */
  constructor (player, options = {}) {
    // the parent class will add player under this.player
    super(player)

    this.#options = this.#safeGetOptions(options)
    this.#currentLanguage = this.#getDefaultLanguage()
    this.#auth = options.auth || null

    // Store chapters data on the player instance for persistence
    if (this.#options.chapters && this.#options.chapters.size > 0) {
      // eslint-disable-next-line no-param-reassign
      player.chaptersData = this.#options.chapters
    }

    const pluginInstance = this
    this.player.ready(function onPlayerReady () {
      this.addClass('vjs-video-chapters')
      pluginInstance.#addButton()
      pluginInstance.#addChapterTextTracks()
    })
  }

  /**
   * @param {?Map} map
   * @returns {Map}
   */
  // eslint-disable-next-line class-methods-use-this
  #safeGetMap (map) {
    return Map.isMap(map) ? map : Map()
  }

  /**
   * @param {?List} list
   * @returns {List}
   */
  // eslint-disable-next-line class-methods-use-this
  #safeGetList (list) {
    return List.isList(list) ? list : List()
  }

  /**
   * make sure that all options are passed or set to a safe state
   * @param {PluginOptions} options
   * @returns {PluginOptions}
   */
  #safeGetOptions (options) {
    const {
      appLanguage,
      chapters,
      staticText,
      userLanguages,
    } = options

    const safeOptions = {
      appLanguage: appLanguage || 'en',
      chapters: this.#safeGetMap(chapters),
      staticText: this.#safeGetMap(staticText),
      userLanguages: this.#safeGetList(userLanguages),
    }

    return safeOptions
  }

  /**
   * Get the default language for chapters
   * @param {Map} chapters - Optional chapters data to use instead of this.#options.chapters
   * @returns {string}
   */
  #getDefaultLanguage (chapters = null) {
    const chaptersData = chapters || this.#options.chapters
    const { userLanguages, appLanguage } = this.#options

    // Handle different data structures
    let availableLanguages = List()
    if (Map.isMap(chaptersData)) {
      availableLanguages = chaptersData.keySeq()
    } else if (chaptersData && typeof chaptersData === 'object') {
      availableLanguages = List(Object.keys(chaptersData))
    }

    // First try user's preferred language
    for (let i = 0; i < userLanguages.size; i += 1) {
      const lang = userLanguages.get(i)
      if (availableLanguages.includes(lang)) {
        return lang
      }
    }

    // Then try app language
    if (availableLanguages.includes(appLanguage)) {
      return appLanguage
    }

    // Finally, use the first available language
    const firstLanguage = availableLanguages.first()
    return firstLanguage || 'en'
  }

  /**
   * add VideoChaptersButton to initialize component tree
   * @returns {void}
   */
  #addButton () {
    const { chapters } = this.#options
    const hasChapters = chapters && chapters.size > 0

    // Only add button if there are chapters
    if (hasChapters) {
      // Check if button already exists to prevent duplicates
      const existingButton = this.player.getChild('ControlBar').getChild('VideoChaptersButton')
      if (!existingButton) {
        const button = new VideoChaptersButton(this.player)
        this.player.getChild('ControlBar').addChild(button, 9) // Add at index 9
      }
    }
  }

  /**
   * Add chapter text tracks to the player
   * @returns {void}
   */
  #addChapterTextTracks () {
    // Get chapters from player stored data if not available in options
    let { chapters } = this.#options
    if (!chapters || chapters.size === 0) {
      chapters = this.player.chaptersData
    }

    const hasChapters = chapters && chapters.size > 0

    // Only add text tracks if there are chapters
    if (hasChapters) {
      // Wait for the player to be ready
      this.player.one('loadedmetadata', () => {
        this.addChapterTextTracks()
      })
    }
  }

  /**
   * Add chapter text tracks and render markers
   * @returns {void}
   */
  addChapterTextTracks () {
    const chapters = this.getChapters()

    if (!chapters || chapters.size === 0) {
      return
    }

    // Check if text tracks already exist to avoid duplicates
    const existingTracks = this.player.textTracks()
    const existingChapterTracks = Array.from(existingTracks).filter((track) => track.kind === 'metadata' && track.label && track.label.includes('English'))

    if (existingChapterTracks.length > 0) {
      // Text tracks already exist, just update the active one
      this.updateActiveChapterTrack()
      this.loadAndRenderChapterMarkers()
      return
    }

    // Add text tracks for each language
    chapters.forEach((vttUrl, language) => {
      this.player.addRemoteTextTrack({
        src: vttUrl,
        kind: 'metadata', // Use 'metadata' instead of 'chapters' to avoid built-in Video.js chapters
        srclang: language,
        label: `Chapters-${this.getLanguageLabel(language)}`, // Prefix with 'Chapters-' to avoid conflicts
        default: language === this.getCurrentLanguage(),
      }, false)
    })

    // Listen for language changes to update the active text track
    this.player.el().addEventListener('languageChanged', () => {
      this.updateActiveChapterTrack()
      this.loadAndRenderChapterMarkers(0)
    })

    // Listen for text track loading
    this.player.on('texttrackchange', () => {
      // Wait a bit for the track to fully load
      setTimeout(() => {
        this.loadAndRenderChapterMarkers(0)
      }, 100)
    })

    // Initial render of chapter markers with a delay to allow text tracks to load
    setTimeout(() => {
      this.loadAndRenderChapterMarkers(0)
    }, 500)
  }

  /**
   * Load and render chapter markers from VTT
   * @param {number} retryCount - Number of retry attempts (internal use)
   * @returns {void}
   */
  loadAndRenderChapterMarkers (retryCount = 0) {
    const chapters = this.getChapters()
    const currentLanguage = this.getCurrentLanguage()

    if (!chapters || chapters.size === 0) {
      return
    }

    // Get the VTT URL for the current language
    const vttUrl = chapters.get(currentLanguage)
    if (!vttUrl) {
      return
    }

    // Use Video.js text track system instead of manual fetching
    const textTracks = this.player.textTracks()

    const chapterTrack = Array.from(textTracks).find((track) => track.kind === 'metadata'
      && track.label
      && track.label === `Chapters-${this.getLanguageLabel(currentLanguage)}`)

    if (chapterTrack && chapterTrack.cues && chapterTrack.cues.length > 0) {
      // Convert cues to chapter format
      const parsedChapters = Array.from(chapterTrack.cues).map((cue) => ({
        time: cue.startTime,
        title: cue.text,
        startTime: cue.startTime,
        endTime: cue.endTime,
      }))

      this.renderChapterMarkers(parsedChapters)
      this.setParsedChapters(parsedChapters)
    } else if (retryCount < 3) {
      // Fallback to manual fetching if text track is not loaded
      // Retry after a short delay
      setTimeout(() => {
        this.loadAndRenderChapterMarkers(retryCount + 1)
      }, 1000)
    } else {
      // Final fallback to manual fetching
      this.#loadChaptersManually(vttUrl)
    }
  }

  /**
   * Fallback method to manually load chapters
   * @param {string} vttUrl - VTT file URL
   * @returns {void}
   */
  #loadChaptersManually (vttUrl) {
    // Import the VTT parser dynamically
    import('./vtt-parser').then(({ getChaptersFromVTT }) => {
      getChaptersFromVTT(vttUrl, this.#auth)
        .then((parsedChapters) => {
          this.renderChapterMarkers(parsedChapters)
          this.setParsedChapters(parsedChapters)
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('Error loading chapters for markers:', error)
        })
    })
  }

  /**
   * Render chapter markers on the progress bar
   * @param {Array} chapters - Array of chapter objects
   * @param {number} retryCount - Number of retry attempts (internal use)
   * @returns {void}
   */
  renderChapterMarkers (chapters, retryCount = 0) {
    // Limit retries to prevent infinite loops
    if (retryCount > 10) {
      return
    }

    if (!chapters || chapters.length === 0) {
      return
    }

    // Get the progress bar element
    const progressBar = this.player.controlBar.getChild('ProgressControl')
    if (!progressBar) {
      // Retry after a short delay if components aren't ready
      setTimeout(() => this.renderChapterMarkers(chapters, retryCount + 1), 100)
      return
    }

    const seekBar = progressBar.getChild('SeekBar')
    if (!seekBar) {
      // Retry after a short delay if components aren't ready
      setTimeout(() => this.renderChapterMarkers(chapters, retryCount + 1), 100)
      return
    }

    const seekBarEl = seekBar.el()
    const duration = this.player.duration()

    // Ensure seekBarEl is a valid DOM element
    if (!seekBarEl || !seekBarEl.querySelectorAll) {
      // Retry after a short delay if DOM isn't ready
      setTimeout(() => this.renderChapterMarkers(chapters, retryCount + 1), 100)
      return
    }

    // Remove existing chapter markers
    const existingMarkers = seekBarEl.querySelectorAll('.vjs-chapter-marker')
    existingMarkers.forEach((marker) => marker.remove())

    // Add new chapter markers
    chapters.forEach((chapter, index) => {
      if (chapter.time > 0 && chapter.time < duration) {
        const marker = document.createElement('div')
        marker.className = 'vjs-chapter-marker'
        marker.style.cssText = `
          position: absolute;
          top: 0;
          bottom: 0;
          width: 2px;
          background-color: rgba(255, 255, 255, 0.6);
          z-index: 10;
          pointer-events: none;
          transition: all 0.2s ease;
        `

        // Calculate position based on chapter time
        const percentage = (chapter.time / duration) * 100
        marker.style.left = `${percentage}%`

        // Add tooltip
        marker.title = chapter.title || `Chapter ${index + 1}`

        seekBarEl.appendChild(marker)
      }
    })
  }

  /**
   * Update the active chapter text track based on current language
   * @returns {void}
   */
  updateActiveChapterTrack () {
    const currentLanguage = this.getCurrentLanguage()
    const textTracks = this.player.textTracks()

    for (let i = 0; i < textTracks.length; i += 1) {
      const track = textTracks[i]
      if (track.kind === 'metadata' && track.label && (track.label.includes('English') || track.label.includes('Español') || track.label.includes('Français'))) {
        if (track.language === currentLanguage) {
          track.mode = 'showing'
        } else {
          track.mode = 'disabled'
        }
      }
    }
  }

  /**
   * Get language label for display
   * @param {string} language - Language code
   * @returns {string} Language label
   */
  // eslint-disable-next-line class-methods-use-this
  getLanguageLabel (language) {
    const labels = {
      en: 'English',
      es: 'Español',
      fr: 'Français',
      de: 'Deutsch',
    }
    return labels[language] || language
  }

  /**
   * @param {string} key to search for
   * @param {string} defaultValue
   * @returns {string}
   */
  getStaticText (key, defaultValue) {
    return this.#options.staticText.get(key, defaultValue)
  }

  /**
   * @returns {Chapters}
   */
  getChapters () {
    // If chapters is undefined, try to get it from the player's stored chapters data
    if (!this.#options.chapters || this.#options.chapters.size === 0) {
      // Try to get chapters from the player's stored data
      const { player } = this
      if (player && player.chaptersData) {
        return player.chaptersData
      }
    }

    return this.#options.chapters || Map()
  }

  /**
   * @returns {string}
   */
  getCurrentLanguage () {
    // First check if we have a language stored on the player instance
    if (this.player.currentChaptersLanguage) {
      return this.player.currentChaptersLanguage
    }

    // If we have a manually set language, use it
    if (this.#currentLanguage && this.#currentLanguage !== 'en') {
      return this.#currentLanguage
    }

    // Otherwise, dynamically determine the best language based on available chapters
    const chapters = this.getChapters()
    if (chapters && chapters.size > 0) {
      return this.#getDefaultLanguage(chapters)
    }

    return this.#currentLanguage || 'en'
  }

  /**
   * Get the authentication object
   * @returns {Object|null} Authentication object with JWT
   */
  getAuth () {
    return this.#auth
  }

  /**
   * @param {string} language
   * @returns {void}
   */
  setCurrentLanguage (language) {
    this.#currentLanguage = language

    // Also store the language on the player instance for persistence across plugin instances
    this.player.currentChaptersLanguage = language
  }

  /**
   * @returns {string}
   */
  getAppLanguage () {
    return this.#options.appLanguage
  }

  /**
   * @returns {UserLanguages}
   */
  getUserLanguages () {
    return this.#options.userLanguages
  }

  /**
   * Close the chapters menu
   * @returns {void}
   */
  closeMenu () {
    const menuButton = this.player.controlBar.getChild('VideoChaptersButton')
    if (menuButton && menuButton.menu) {
      menuButton.menu.hide()
    }
  }

  /**
   * Get the current chapter based on playback time
   * @returns {Object|null}
   */
  getCurrentChapter () {
    const currentTime = this.player.currentTime()
    const chapters = this.getParsedChapters()

    if (!chapters || chapters.length === 0) {
      return null
    }

    // Find the chapter that contains the current time
    for (let i = chapters.length - 1; i >= 0; i -= 1) {
      const chapter = chapters[i]
      if (currentTime >= chapter.time) {
        return chapter
      }
    }

    return chapters[0] // Default to first chapter if before any chapter
  }

  /**
   * Get chapters for the current language
   * @returns {Array}
   */
  getChaptersForCurrentLanguage () {
    const currentLanguage = this.getCurrentLanguage()
    const chapters = this.getChapters()

    if (!chapters || chapters.isEmpty()) {
      return []
    }

    const languageChapters = chapters.get(currentLanguage)
    if (!languageChapters) {
      return []
    }

    // If it's a VTT URL, we need to parse it
    if (typeof languageChapters === 'string') {
      // For now, return empty array - the chapters column will handle VTT parsing
      return []
    }

    return languageChapters.toArray()
  }

  /**
   * Store parsed chapters for current language (called by chapters column)
   * @param {Array} chapters - Parsed chapter data
   * @returns {void}
   */
  setParsedChapters (chapters) {
    this.player.currentParsedChapters = chapters
  }

  /**
   * Get parsed chapters for current language
   * @returns {Array}
   */
  getParsedChapters () {
    return this.player.currentParsedChapters || []
  }

  /**
   * Register the plugin with Video.js
   * @param {Object} videojsInstance - Video.js instance
   * @returns {void}
   */
  static register (videojsInstance) {
    const registerPlugin = videojsInstance.registerPlugin || videojsInstance.plugin
    registerPlugin(VideoChaptersPlugin.PLUGIN_NAME, VideoChaptersPlugin)

    // Register the button component
    videojsInstance.registerComponent('VideoChaptersButton', VideoChaptersButton)
  }

  /**
   * Override dispose to prevent the prototype error
   * @returns {void}
   */
  // eslint-disable-next-line class-methods-use-this
  dispose () {
    // Don't call super.dispose() to avoid the prototype error
    // Let Video.js handle the cleanup automatically
  }
}
